import {
  getListOfRoles,
  getListofResponsibility,
  getListofResponsibilityV2,
  getListOfRolesV2,
  createRole,
  createResponsibility,
  getListOfRolesAttachedToPosition,
  getListOfResponsibilitiesAttachedToRole,
} from "@/api";
import {
  getRoles,
  updateRoles,
  getResponsibilities,
  updateResponsibilities,
} from "@components/helper/localStorageHelper";
import { createLatestRequestGuard, stableKey } from "@components/utils/createLatestRequestGuard";

// ER-11705: the Organization > Responsibilities tab loads through this Vuex
// action (not the Pinia store), and the account/role-style over-fetch shows up
// here too. Collapse concurrent identical loads into one request and drop a
// superseded response so the newest filter always wins.
const loadResponsibilitiesV2Guard = createLatestRequestGuard();

export default {
  namespaced: true,
  state: {
    roles: [],
    responsibilities: [],
    responsibilitiesCount: 0,
    /** ER-10524 — filter-aware indicator counts returned by the v2 responsibility list endpoint. */
    responsibilitiesIndicators: null,
    ordering:
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("readinessCatalogs.ordering.roles")) ||
      "name",
    responsibilitiesOrdering:
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("readinessCatalogs.ordering.responsibilities")) ||
      "name",
  },
  mutations: {
    setRoles(state, data) {
      state.roles = data;
    },
    setResponsibilities(state, data) {
      state.responsibilities = data;
    },
    setResponsibilitiesCount(state, data) {
      state.responsibilitiesCount = data;
    },
    setResponsibilitiesIndicators(state, data) {
      state.responsibilitiesIndicators = data;
    },
    setRolesCount(state, data) {
      state.rolesCount = data;
    },
    setOrdering(state, ordering) {
      state.ordering = ordering;
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("readinessCatalogs.ordering.roles", ordering);
        }
      } catch (e) {
        // ignore storage errors
      }
    },
    setResponsibilitiesOrdering(state, ordering) {
      state.responsibilitiesOrdering = ordering;
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("readinessCatalogs.ordering.responsibilities", ordering);
        }
      } catch (e) {
        // ignore storage errors
      }
    },
  },
  getters: {
    allRoles: (state) => state.roles,
    allResponsibilities: (state) => state.responsibilities,
    responsibilitiesCount: (state) => state.responsibilitiesCount,
    responsibilitiesIndicators: (state) => state.responsibilitiesIndicators,
    rolesCount: (state) => state.rolesCount,
    ordering: (state) => state.ordering,
    responsibilitiesOrdering: (state) => state.responsibilitiesOrdering,
    getRoleById: (state) => {
      return (roleId) => {
        const role = state.roles.find((r) => r.id == roleId);
        if (role && !role.code_str) {
          role.code_str = `#${role.code} - ${role.name}`;
        }
        return role;
      };
    },
    getAllRolesAndAttachedResponsibilities: (state) => {
      const rolesWithParentId = state.roles.map((role) => {
        // Check if code_str is null or does not exist
        if (!role?.code_str) {
          role.code_str = `#${role.code} - ${role.name}`;
        }
        return {
          ...role,
          parentId: -1,
        };
      });

      const responsibilitiesWithParentId = state.responsibilities.map((responsibility) => {
        const attachedRole = responsibility.attached_roles.find((attachment) =>
          state.roles.some((role) => role.id === attachment.id)
        );
        // Check if code_str is null or does not exist
        if (!responsibility?.code_str) {
          responsibility.code_str = `#${responsibility.code} - ${responsibility.name}`;
        }
        return {
          ...responsibility,
          parentId: attachedRole ? attachedRole.id : null,
        };
      });

      return [...rolesWithParentId, ...responsibilitiesWithParentId];
    },
    getResponsibilitiesFromRoleId: (state) => (roleId) => {
      return state.responsibilities
        .filter((responsibility) => {
          return responsibility.attached_roles.some((attachment) => attachment.id === roleId);
        })
        .map((responsibility) => {
          // Check if code_str is null or does not exist
          if (!responsibility?.code_str) {
            responsibility.code_str = `#${responsibility.code} - ${responsibility.name}`;
          }
          return {
            ...responsibility,
            parentId: roleId,
          };
        });
    },
  },
  actions: {
    setOrdering({ commit }, ordering) {
      commit("setOrdering", ordering);
    },
    setResponsibilitiesOrdering({ commit }, ordering) {
      commit("setResponsibilitiesOrdering", ordering);
    },
    async loadRoles({ commit }) {
      try {
        let rolesList = getRoles();
        commit("setRoles", rolesList);
        const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
        await getListOfRoles(accountId, 1, 500)
          .then((response) => {
            if (response.data && response.data.results) {
              const results = response.data.results.map((role) => {
                // Check if code_str is null or does not exist
                if (!role?.code_str) {
                  role.code_str = `#${role.code} - ${role.name}`;
                }
                return {
                  ...role,
                };
              });
              updateRoles(results);
              commit("setRoles", results);
            }
          })
          .catch((error) => {
            console.error("error fetching employees: ", error);
          });
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
    async loadRolesV2({ commit, state }, { limit, offset, search, filters = {}, ordering = null }) {
      try {
        let rolesList = getRoles();
        commit("setRoles", rolesList);
        const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
        await getListOfRolesV2(
          accountId,
          limit,
          offset,
          search,
          filters,
          ordering ?? state.ordering
        )
          .then((response) => {
            commit("setRolesCount", response?.data?.count);
            if (response.data && response.data.results) {
              const results = response.data.results.map((role) => {
                // Check if code_str is null or does not exist
                if (!role?.code_str) {
                  role.code_str = `#${role.code} - ${role.name}`;
                }
                return {
                  ...role,
                };
              });
              updateRoles(results);
              commit("setRoles", results);
            }
          })
          .catch((error) => {
            console.error("error fetching employees: ", error);
          });
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
    async loadResponsibilities({ commit }) {
      try {
        let responsibilitiesList = getResponsibilities();
        commit("setResponsibilities", responsibilitiesList);
        const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
        await getListofResponsibility(accountId, 1, 500)
          .then((response) => {
            if (response.data && response.data.results) {
              const results = response.data.results.map((responsibility) => {
                // Check if code_str is null or does not exist
                if (!responsibility?.code_str) {
                  responsibility.code_str = `#${responsibility.code} - ${responsibility.name}`;
                }
                return {
                  ...responsibility,
                };
              });
              updateResponsibilities(results);
              commit("setResponsibilities", results);
            }
          })
          .catch((error) => {
            console.error("error fetching employees: ", error);
          });
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
    async loadResponsibilitiesV2(
      { commit, state },
      { limit, offset, search, filters = {}, ordering = null, include_badge_holders = false }
    ) {
      let accountId;
      try {
        accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      } catch (e) {
        console.log("error fetching employees: ", e);
        return;
      }

      const effectiveOrdering = ordering ?? state.responsibilitiesOrdering;
      const key = stableKey({
        accountId,
        limit,
        offset,
        search,
        filters,
        ordering: effectiveOrdering,
        include_badge_holders,
      });

      const run = loadResponsibilitiesV2Guard.start(key, async (entry) => {
        let responsibilitiesList = getResponsibilities();
        commit("setResponsibilities", responsibilitiesList);

        // If searchTerm is provided, filter the results
        let response;
        try {
          response = await getListofResponsibilityV2(
            accountId,
            limit,
            offset,
            search,
            filters,
            effectiveOrdering,
            include_badge_holders
          );
        } catch (error) {
          console.error("error fetching employees: ", error);
          return;
        }

        // A newer request (different key) may have started while this one was in
        // flight. Discard this stale response so the newest filter always wins.
        if (!entry.isCurrent()) {
          return;
        }

        commit("setResponsibilitiesCount", response?.data?.count);

        // ER-10524 — capture filter-aware indicator counts (present when the
        // backend `idea-MDP-558` flag is enabled); absent/undefined counts
        // resolve to 0 so the header dashboard never renders `null`.
        commit("setResponsibilitiesIndicators", {
          activeCount: response?.data?.active_count ?? 0,
          draftCount: response?.data?.draft_count ?? 0,
          noChecklistCount: response?.data?.no_checklist_count ?? 0,
          noRoleAttachedCount: response?.data?.no_role_attached_count ?? 0,
          notUsedCount: response?.data?.not_used_count ?? 0,
        });

        if (response.data && response.data.results) {
          let results = response.data.results.map((responsibility) => {
            // Check if code_str is null or does not exist
            if (!responsibility?.code_str) {
              responsibility.code_str = `#${responsibility.code} - ${responsibility.name}`;
            }
            return {
              ...responsibility,
            };
          });

          updateResponsibilities(results);
          commit("setResponsibilities", results);
        }
      });

      try {
        await run.promise;
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
    async createRole(context, { role, accountId }) {
      try {
        const { data } = await createRole(accountId, role);
        if (data) {
          return data;
        }
      } catch (e) {
        console.log("e: ", e);
      }
    },
    async createResponsibility(context, { name, accountId }) {
      try {
        const payload = {
          name: name,
          account: accountId,
        };
        const newRes = await createResponsibility(accountId, payload);
        return newRes;
      } catch (e) {
        console.log("e: ", e);
      }
    },
    async loadRolesAttachedToPosition(
      { commit },
      { limit, offset, search, filters = {}, positionId }
    ) {
      try {
        let rolesList = getRoles();
        commit("setRoles", rolesList);
        const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
        await getListOfRolesAttachedToPosition(
          accountId,
          limit,
          offset,
          search,
          positionId,
          filters
        )
          .then((response) => {
            commit("setRolesCount", response?.data?.count);
            if (response.data && response.data.results) {
              const results = response.data.results.map((role) => {
                // Check if code_str is null or does not exist
                if (!role?.code_str) {
                  role.code_str = `#${role.code} - ${role.name}`;
                }
                return {
                  ...role,
                };
              });
              updateRoles(results);
              commit("setRoles", results);
            }
          })
          .catch((error) => {
            console.error("error fetching employees: ", error);
          });
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
    async loadResponsibilitiesAttachedToRole(
      { commit },
      { limit, offset, search, filters = {}, roleId, include_badge_holders = false }
    ) {
      try {
        let rolesList = getRoles();
        commit("setRoles", rolesList);
        const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
        await getListOfResponsibilitiesAttachedToRole(
          accountId,
          limit,
          offset,
          search,
          roleId,
          filters,
          include_badge_holders
        )
          .then((response) => {
            commit("setResponsibilitiesCount", response?.data?.count);
            if (response.data && response.data.results) {
              const results = response.data.results.map((responsibility) => {
                // Check if code_str is null or does not exist
                if (!responsibility?.code_str) {
                  responsibility.code_str = `#${responsibility.code} - ${responsibility.name}`;
                }
                return {
                  ...responsibility,
                };
              });
              updateResponsibilities(results);
              commit("setResponsibilities", results);
            }
          })
          .catch((error) => {
            console.error("error fetching employees: ", error);
          });
      } catch (e) {
        console.log("error fetching employees: ", e);
      }
    },
  },
};
